import React, { useMemo } from 'react';
import { cj, cm } from 'src/utils/classNames';
import { MarketType } from '@drift-labs/sdk';
import useModal from 'src/components/Modals/hooks/useModal';
import Tag from 'src/components/primitives/Tag/Tag';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import { WithClassName } from 'src/utils/UtilityTypes';

type MarketCellDirection = 'long' | 'short' | 'buy' | 'sell' | 'neutral';

interface MarketCellV2Props extends WithClassName {
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
	direction?: MarketCellDirection;
	marketSymbol: string;
	marketType: MarketType;
	textClass?: string;
	marketIndex: number;
	hideLeverage?: boolean;
	hidePositionType?: boolean;
	isIsolated?: boolean;
}

const MarketCellV2 = ({
	className,
	onClick,
	direction,
	marketSymbol,
	marketType,
	textClass,
	marketIndex,
	hideLeverage,
	hidePositionType,
	isIsolated,
}: MarketCellV2Props) => {
	const marketName = useMemo(() => {
		return marketSymbol.replace(/-PERP$/i, '').replace(/-SPOT$/i, '');
	}, [marketSymbol]);

	const marketTypeLabel = useMemo(() => {
		if (marketType === MarketType.SPOT) return 'Spot';
		return isIsolated ? 'Isolated' : 'Cross';
	}, [marketType, isIsolated]);
	const isLong = direction === 'long' || direction === 'buy';
	const isNeutral = direction === 'neutral' || direction === undefined;

	const { show } = useModal();

	// For MarketCellV2, when positionMaxMarginRatio is undefined,
	// show the market's max margin ratio, NOT the saved value
	const displayLeverage = useGetDisplayLeverageForMarket(marketIndex);

	const openEditPositionMaxLeverage = () => {
		show({
			type: 'position_max_leverage',
			props: {
				marketIndex,
			},
		});
	};

	return (
		<div
			className={cm(
				'relative flex w-full flex-row items-center py-2 pr-3 pl-3',
				onClick && 'cursor-pointer',
				className
			)}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
		>
			<div
				className={cj(
					'absolute top-1/2 left-0 h-9 w-xs -translate-y-1/2 rounded-r-xs',
					isNeutral
						? 'bg-text-tertiary'
						: isLong
							? 'bg-text-positive-green-button'
							: 'bg-text-negative-red-button'
				)}
			/>

			<div className="-mr-3xl flex flex-col items-start justify-center gap-xs pr-3xl">
				<span
					className={cm(
						'line-clamp-1 typo-t4 text-text-default',
						isNeutral
							? 'text-text-default'
							: isLong
								? 'text-text-positive-green-button'
								: 'text-text-negative-red-button',
						textClass
					)}
				>
					{marketName.toUpperCase()}
				</span>
				<div className="flex items-center gap-x-xs">
					{!hidePositionType && (
						<Tag className="bg-button-secondary-bg-hover text-text-default">
							{marketTypeLabel}
						</Tag>
					)}

					{!hideLeverage && (
						<button
							className="-m-xs p-xs"
							onClick={(e) => {
								e.stopPropagation();
								openEditPositionMaxLeverage();
							}}
						>
							<Tag className="bg-purple-bg text-purple-link hover:bg-button-selector">
								{Math.round(displayLeverage)}x
							</Tag>
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default MarketCellV2;
